import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Activity, Users, GraduationCap, Rocket, Truck, Store, Building2, Network, ClipboardList, Shield, Image as ImageIcon, AlertTriangle } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const TILES = [
  { to: "/admin/ecosystem-map", icon: Network, label: "Ecosystem map", blurb: "Live topology of every node + their relationships." },
  { to: "/admin/audit-feed",    icon: ClipboardList, label: "Audit feed", blurb: "Time-stamped mutation log across all phases." },
  { to: "/admin/role-hub",      icon: Shield, label: "Role hub", blurb: "Multi-role identity console. Master-admin only mutation." },
  { to: "/admin/brand-assets",  icon: ImageIcon, label: "Brand assets", blurb: "Logos, mascots, watermarks, OG templates." },
];

function Stat({ label, value, sub, tone = "" }) {
  return (
    <div className={`glass-panel p-5 ${tone}`} data-testid={`cc-stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
      <div className="eyebrow !text-lifted-text-dim text-[0.6rem]">{label}</div>
      <div className="font-anton text-4xl text-lifted-green mt-1">{value}</div>
      {sub && <div className="text-xs text-lifted-dim mt-1">{sub}</div>}
    </div>
  );
}

export default function AdminCommandCenter() {
  const [s, setS] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [a, b] = await Promise.all([
          api.get("/admin/command-center/summary"),
          api.get("/admin/compliance-alerts"),
        ]);
        setS(a.data); setAlerts(b.data);
      } catch (e) { setErr(apiErrorMessage(e)); }
    })();
  }, []);

  if (err) return <div className="text-rose-300" data-testid="cc-error">{err}</div>;
  if (!s) return <div className="font-bebas tracking-[0.3em] text-lifted-dim">LOADING COMMAND CENTER…</div>;

  return (
    <div data-testid="admin-command-center" className="space-y-10">
      <header className="fade-up">
        <div className="eyebrow flex items-center gap-2"><Activity className="h-3 w-3" /> Master Command</div>
        <h1 className="headline-xl">Command Center</h1>
        <p className="text-lifted-dim mt-2 max-w-3xl text-sm">
          Cross-module pulse for the entire Lifted ecosystem. All data is pulled live from existing collections — no duplicate sources, no separate dispatch board.
        </p>
      </header>

      {/* KPI grid */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Users (active)" value={s.users.active} sub={`${s.users.total} total · ${s.users.suspended} suspended`} />
        <Stat label="Drivers (available)" value={s.delivery.available} sub={`${s.delivery.drivers} drivers · ${s.delivery.suspended} suspended`} />
        <Stat label="Partners (approved)" value={s.partner.approved} sub={`${s.partner.total} total · ${s.partner.pending} pending · ${s.partner.suspended} suspended`} />
        <Stat label="Franchises (approved)" value={s.franchise.approved} sub={`${s.franchise.total} total · ${s.franchise.territories} territories · ${s.franchise.partners_attached} partners attached`} />
        <Stat label="Active orders" value={s.delivery.active_orders} sub={`${s.delivery.pending_orders} pending`} />
        <Stat label="Affiliates" value={s.affiliate.profiles} sub={`${s.affiliate.commission_pending} commissions pending`} />
        <Stat label="Certifications" value={s.training.certifications_active} sub="active (non-revoked)" />
        <Stat label="Compliance alerts" value={alerts?.total ?? 0} sub="across drivers, partners, franchises, zones, orders" />
      </section>

      {/* Module deeplinks */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {TILES.map((t, i) => (
          <Link key={t.to} to={t.to} data-testid={`cc-tile-${t.to.split("/").pop()}`}
                className={`glass-panel p-5 fade-up delay-${(i % 4) + 1} hover:-translate-y-1 hover:border-lifted-green/30 transition-transform`}>
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl border border-lifted-green/30 bg-lifted-green/10 text-lifted-green flex items-center justify-center">
                <t.icon className="h-5 w-5" />
              </div>
              <div>
                <div className="font-anton text-xl uppercase">{t.label}</div>
                <div className="text-xs text-lifted-dim">{t.blurb}</div>
              </div>
            </div>
          </Link>
        ))}
      </section>

      {/* Per-phase snapshots — link cards into existing module admin pages (no duplicate UI) */}
      <section className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="cc-phase-snapshots">
        <Link to="/admin/training" className="glass-panel p-5 hover:-translate-y-1 transition-transform">
          <div className="flex items-center gap-3"><GraduationCap className="h-5 w-5 text-lifted-green" />
            <div><div className="font-anton text-lg uppercase">Training</div>
            <div className="text-xs text-lifted-dim">{s.training.certifications_active} active certs</div></div></div>
        </Link>
        <Link to="/admin/affiliate" className="glass-panel p-5 hover:-translate-y-1 transition-transform">
          <div className="flex items-center gap-3"><Rocket className="h-5 w-5 text-lifted-green" />
            <div><div className="font-anton text-lg uppercase">Affiliate</div>
            <div className="text-xs text-lifted-dim">{s.affiliate.profiles} profiles · {s.affiliate.commission_pending} commissions pending</div></div></div>
        </Link>
        <Link to="/admin/delivery/dispatch" className="glass-panel p-5 hover:-translate-y-1 transition-transform">
          <div className="flex items-center gap-3"><Truck className="h-5 w-5 text-lifted-green" />
            <div><div className="font-anton text-lg uppercase">Delivery</div>
            <div className="text-xs text-lifted-dim">{s.delivery.active_orders} in-flight · {s.delivery.pending_orders} pending</div></div></div>
        </Link>
        <Link to="/admin/partners" className="glass-panel p-5 hover:-translate-y-1 transition-transform">
          <div className="flex items-center gap-3"><Store className="h-5 w-5 text-lifted-green" />
            <div><div className="font-anton text-lg uppercase">Partners</div>
            <div className="text-xs text-lifted-dim">{s.partner.approved}/{s.partner.total} approved</div></div></div>
        </Link>
        <Link to="/admin/franchises" className="glass-panel p-5 hover:-translate-y-1 transition-transform">
          <div className="flex items-center gap-3"><Building2 className="h-5 w-5 text-lifted-green" />
            <div><div className="font-anton text-lg uppercase">Franchise</div>
            <div className="text-xs text-lifted-dim">{s.franchise.approved}/{s.franchise.total} approved · {s.franchise.territories} territories</div></div></div>
        </Link>
        <Link to="/admin/users" className="glass-panel p-5 hover:-translate-y-1 transition-transform">
          <div className="flex items-center gap-3"><Users className="h-5 w-5 text-lifted-green" />
            <div><div className="font-anton text-lg uppercase">Users</div>
            <div className="text-xs text-lifted-dim">{s.users.active}/{s.users.total} active</div></div></div>
        </Link>
      </section>

      {/* Top compliance alerts */}
      <section className="glass-panel p-5" data-testid="cc-alerts-section">
        <div className="flex items-center justify-between mb-3">
          <div className="eyebrow !text-lifted-text-dim flex items-center gap-2"><AlertTriangle className="h-3 w-3" /> Top compliance alerts</div>
          <span className="text-xs text-lifted-mute">{alerts?.total ?? 0} total</span>
        </div>
        <ul className="text-sm divide-y divide-white/5">
          {(alerts?.alerts || []).slice(0, 10).map((a, idx) => (
            <li key={idx} data-testid={`cc-alert-${idx}`} className="py-2 flex items-center justify-between">
              <div>
                <div className="text-lifted-text">{a.label}</div>
                <div className="text-xs text-lifted-dim">{a.kind} · {a.detail}</div>
              </div>
              <span className={`chip ${a.severity === "critical" ? "" : a.severity === "warning" ? "chip--purple" : "chip--green"}`}>{a.severity}</span>
            </li>
          ))}
          {(alerts?.total ?? 0) === 0 && <li className="py-4 text-lifted-mute text-xs text-center">No active compliance alerts. The ecosystem is green.</li>}
        </ul>
      </section>
    </div>
  );
}
